import React, { forwardRef, PropsWithChildren } from 'react';
import { StyleSheet, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';

import { BodyText, Container, DisplayText, theme, useResponsive } from '@sg/garnish';
import type { AllergenTypes } from '@sg/shared-fe-features-menu';

import { useHomeContainerSpacing, type HomeProductCategory } from '../../hooks';
import { HomeFilterableCategory } from '../HomeCategory';
import { HomeContainer } from '../HomeContainer';
import { HomeFilterChip } from '../HomeFilterChip';
import { HomeProductCard } from '../HomeProductCard';

// ─────────────────────────────────────────────────────────────────────────────

export const HomeFilterableCategories = forwardRef<View, HomeFilterableCategoriesProps>(
  (props, ref) => {
    const {
      title,
      filterableCategories,
      dietaryPreferences,
      selectedCategory,
      onSelectCategory,
      openMenu,
      openProductDetails,
      addProductToBag,
      trackItemSelection,
    } = props;

    const { currentBreakpoint, match, minWidth } = useResponsive();
    const containerStyle = useHomeContainerSpacing();

    // ─────────────────────────────────────────────────────────────────────────

    return (
      <Container ref={ref} style={match([styles.containerXS, styles.containerSM])}>
        {currentBreakpoint.isXS ? (
          <BodyText bold style={containerStyle} sizeMatch={['18']}>
            {title}
          </BodyText>
        ) : (
          <DisplayText style={styles.titleSM} sizeMatch={['32']}>
            {title}
          </DisplayText>
        )}

        <FiltersWrapper>
          {filterableCategories.map((category) => (
            <HomeFilterChip
              key={category.id}
              category={category}
              isSelected={selectedCategory?.categoryName === category.categoryName}
              onPress={onSelectCategory}
            />
          ))}
        </FiltersWrapper>

        {selectedCategory ? (
          <HomeContainer.AnimatedContent>
            <HomeFilterableCategory>
              {selectedCategory?.products?.map((product) => (
                <HomeProductCard
                  withMaxWidth={minWidth.isSM}
                  key={product.lineageKey}
                  isEnriched={product.isEnriched}
                  categoryName={selectedCategory.categoryName}
                  id={product.id}
                  slug={product.slug}
                  title={product.title}
                  cost={product.cost}
                  description={product.description}
                  deepLink={product.deepLink}
                  cloudinaryImage={product.cloudinaryImage}
                  contentfulImage={product.contentfulImage}
                  isModifiable={product.isModifiable}
                  dietaryPreferences={dietaryPreferences}
                  allergenProperties={product.allergenProperties}
                  calories={selectedCategory.showCalories ? product.calories : 0}
                  calorieOverrideText={product.calorieOverrideText}
                  totalFatG={selectedCategory.showFat ? product.totalFatG : 0}
                  totalCarbsG={selectedCategory.showCarbs ? product.totalCarbsG : 0}
                  dietaryFiberG={selectedCategory.showFiber ? product.dietaryFiberG : 0}
                  sugarsG={selectedCategory.showSugars ? product.sugarsG : 0}
                  proteinG={selectedCategory.showProtein ? product.proteinG : 0}
                  co2eKg={selectedCategory.showCo2 ? product.co2eKg : 0}
                  openMenu={openMenu}
                  openProductDetails={openProductDetails}
                  addProductToBag={addProductToBag}
                  trackItemSelection={trackItemSelection}
                />
              ))}
            </HomeFilterableCategory>
          </HomeContainer.AnimatedContent>
        ) : null}
      </Container>
    );
  },
);

const FiltersWrapper = (props: PropsWithChildren) => {
  const { currentBreakpoint } = useResponsive();
  const containerStyle = useHomeContainerSpacing();

  if (currentBreakpoint.isXS) {
    return (
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={[styles.filtersXS, containerStyle]}
      >
        {props.children}
      </ScrollView>
    );
  }

  return <View style={styles.filtersSM}>{props.children}</View>;
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    paddingHorizontal: 0,
    gap: theme.spacing['4'],
  },
  containerSM: {
    alignItems: 'center',
    gap: theme.spacing['8'],
  },
  titleSM: {
    color: theme.colors.KALE,
    textAlign: 'center',
  },
  filtersXS: {
    flexDirection: 'row',
    gap: theme.spacing['2'],
  },
  filtersSM: {
    maxWidth: 720,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing['2'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeFilterableCategoriesProps = {
  title: string;
  filterableCategories: HomeProductCategory[];
  dietaryPreferences: AllergenTypes;
  selectedCategory: HomeProductCategory | null;
  onSelectCategory: (category: HomeProductCategory | null) => void;
  openMenu: () => void;
  openProductDetails: (slug: string) => void;
  addProductToBag: (id: string, title: string) => Promise<void>;
  trackItemSelection: (product: string, category: string) => void;
};
